import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Inbox } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import RefreshBar from "@/components/RefreshBar";
import StatusPill from "@/components/StatusPill";
import AdminBaldachinDrawer from "@/components/AdminBaldachinDrawer";
import { InlineLoader } from "@/components/Spinner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { initials } from "@/lib/status";

const META = {
  free: { title: "Free Baldachins", test: (b) => b.status === "free" },
  taken: { title: "Taken Baldachins", test: (b) => b.status === "taken" },
  sold_multiple: { title: "Sold Multiple Times", test: (b) => b.status === "sold_multiple" },
  actions: { title: "Total Sale Actions Today", test: (b) => b.count > 0 },
};

export default function StatsDetail() {
  const { category } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const meta = META[category] || META.taken;

  const load = useCallback((spin = false) => {
    if (spin) setLoading(true);
    api.get("/map").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(true); }, [load]);

  const rows = useMemo(() => {
    const q = search.trim();
    return (data?.baldachins || []).filter(meta.test).filter((b) => !q || String(b.number).includes(q));
  }, [data, meta, search]);

  if (loading && !data) return <InlineLoader label="Loading…" />;

  return (
    <div className="space-y-4 bb-fade-up">
      <button onClick={() => navigate("/admin/dashboard")} className="flex items-center gap-1.5 text-sm font-semibold text-stone-500 hover:text-stone-800" data-testid="stats-back">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </button>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">{meta.title}</h1>
          <p className="text-sm text-stone-500">{rows.length} baldachin{rows.length === 1 ? "" : "s"}</p>
        </div>
        <RefreshBar lastUpdated={data?.last_updated} onRefresh={() => load(true)} loading={loading} testid="stats-refresh" />
      </div>

      <div className="relative max-w-xs">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
        <Input data-testid="stats-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search number…" className="h-10 rounded-xl pl-9" />
      </div>

      {rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-stone-300 bg-white py-16 text-stone-400" data-testid="stats-empty">
          <Inbox className="h-9 w-9" strokeWidth={1.8} />
          <p className="text-sm font-medium">Nothing to show here.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-stone-200 bg-white">
          <div className="bb-scroll overflow-x-auto">
          <table className="w-full min-w-[680px] text-sm" data-testid="stats-table">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50 text-left text-xs uppercase tracking-wide text-stone-500">
                <th className="px-4 py-3 font-semibold">Baldachin</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Sales</th>
                <th className="px-4 py-3 font-semibold">Last worker</th>
                <th className="px-4 py-3 font-semibold">Last action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr
                  key={b.id}
                  onClick={() => { setSelectedId(b.id); setDrawerOpen(true); }}
                  className="cursor-pointer border-b border-stone-100 last:border-0 hover:bg-stone-50"
                  data-testid={`stats-row-${b.number}`}
                >
                  <td className="px-4 py-3 font-heading text-base font-bold">#{b.number}</td>
                  <td className="px-4 py-3"><StatusPill status={b.status} /></td>
                  <td className="px-4 py-3 font-bold">{b.count}x</td>
                  <td className="px-4 py-3">
                    {b.last_worker_name ? (
                      <span className="flex items-center gap-2">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-stone-900 text-[10px] font-bold text-white">{initials(b.last_worker_name)}</span>
                        {b.last_worker_name}
                      </span>
                    ) : <span className="text-stone-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-stone-500">{b.last_action_time_label || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <AdminBaldachinDrawer baldachinId={selectedId} date={data?.date} open={drawerOpen} onOpenChange={setDrawerOpen} onChanged={() => load()} />
    </div>
  );
}
